import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import toast from 'react-hot-toast';
import { GuardData } from '../../manage_guard/service/guardService';
import {
    getMonthlyAttendance,
    markAttendance,
    getCurrentDateInfo,
    getDaysInMonth,
    formatDate,
    AttendanceStatus,
    MonthlyAttendance,
    calculateMonthlySummary,
} from '../service/attendanceService';

const GuardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  margin-bottom: ${theme.spacing[4]};
`;

const GuardImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
`;

const GuardDetails = styled.div`
  flex: 1;
`;

const GuardName = styled.div`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin-bottom: ${theme.spacing[1]};
`;

const GuardMeta = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
`;

const DayHeader = styled.div`
  text-align: center;
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.secondary};
  padding: ${theme.spacing[2]};
`;

const DayCell = styled.button<{
    status: AttendanceStatus;
    disabled: boolean;
    isToday: boolean;
}>`
  aspect-ratio: 1;
  border: 2px solid ${({ status, isToday }) => {
        if (isToday) return theme.colors.primary[500];
        if (status === 'present') return theme.colors.accent.success;
        if (status === 'absent') return theme.colors.accent.danger;
        if (status === 'leave') return theme.colors.accent.warning;
        if (status === 'undeployed') return theme.colors.light.text.muted;
        return theme.colors.light.border.medium;
    }};
  background-color: ${({ status, disabled }) => {
        if (disabled) return theme.colors.light.background.secondary;
        if (status === 'present') return `${theme.colors.accent.success}10`;
        if (status === 'absent') return `${theme.colors.accent.danger}10`;
        if (status === 'leave') return `${theme.colors.accent.warning}10`;
        if (status === 'undeployed') return theme.colors.light.background.tertiary;
        return theme.colors.white;
    }};
  border-radius: ${theme.borderRadius.md};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[1]};

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.md};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DayNumber = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
`;

const StatusBadge = styled.div<{ status: AttendanceStatus }>`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  margin-top: ${theme.spacing[1]};
  color: ${({ status }) => {
        if (status === 'present') return theme.colors.accent.success;
        if (status === 'absent') return theme.colors.accent.danger;
        if (status === 'leave') return theme.colors.accent.warning;
        if (status === 'undeployed') return theme.colors.light.text.muted;
        return theme.colors.light.text.secondary;
    }};
`;

const Legend = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: center;
  margin-top: ${theme.spacing[4]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.light.background.secondary};
  border-radius: ${theme.borderRadius.md};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: ${theme.borderRadius.sm};
  background-color: ${({ color }) => color};
`;

interface AttendanceModalProps {
    guard: GuardData;
    isOpen: boolean;
    onClose: () => void;
    onUpdate: () => void;
    companyId: string;
    year: number;
    month: number;
}

const AttendanceModal: React.FC<AttendanceModalProps> = ({
    guard,
    isOpen,
    onClose,
    onUpdate,
    companyId,
    year,
    month,
}) => {
    const [attendance, setAttendance] = useState<MonthlyAttendance | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [changes, setChanges] = useState<{ [date: string]: AttendanceStatus }>({});
    const [summary, setSummary] = useState<any>(null);

    const currentDateInfo = getCurrentDateInfo();
    const daysInMonth = getDaysInMonth(year, month);

    const checkIsDeployed = (day: number): boolean => {
        const testDate = new Date(year, month - 1, day);
        testDate.setHours(0, 0, 0, 0);

        const parseDate = (dateVal: any): Date | null => {
            if (!dateVal) return null;
            if (dateVal instanceof Date) return dateVal;
            if (typeof dateVal.toDate === 'function') return dateVal.toDate();
            if (dateVal.seconds) return new Date(dateVal.seconds * 1000);

            // Handle string formats
            if (typeof dateVal === 'string') {
                const dmyMatch = dateVal.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                if (dmyMatch) {
                    return new Date(parseInt(dmyMatch[3]), parseInt(dmyMatch[2]) - 1, parseInt(dmyMatch[1]));
                }
                const d = new Date(dateVal);
                return isNaN(d.getTime()) ? null : d;
            }
            return null;
        };

        const deploymentPeriods: { start: Date; end: Date | null }[] = [];

        if (guard.currentDeployment?.postDate) {
            const start = parseDate(guard.currentDeployment.postDate);
            const end = parseDate(guard.currentDeployment.endDate);
            if (start) deploymentPeriods.push({ start, end });
        }

        if (guard.history && Array.isArray(guard.history)) {
            guard.history.forEach((h: any) => {
                const start = parseDate(h.postDate);
                const end = parseDate(h.endDate) || parseDate(h.completedAt);
                if (start) deploymentPeriods.push({ start, end });
            });
        }

        return deploymentPeriods.some((period) => {
            const periodStart = new Date(period.start);
            periodStart.setHours(0, 0, 0, 0);

            const periodEnd = period.end ? new Date(period.end) : null;
            if (periodEnd) periodEnd.setHours(23, 59, 59, 999);

            if (periodEnd) {
                return testDate >= periodStart && testDate <= periodEnd;
            }
            return testDate >= periodStart;
        });
    };

    useEffect(() => {
        if (isOpen && guard.id) {
            loadAttendance();
        }
    }, [isOpen, guard.id, year, month]);

    const loadAttendance = async () => {
        setLoading(true);
        try {
            const data = await getMonthlyAttendance(
                guard.id,
                year,
                month,
                companyId
            );
            const summaryData = await calculateMonthlySummary(
                guard,
                year,
                month,
                companyId
            );
            setAttendance(data);
            setSummary(summaryData);
            setChanges({});
        } catch (error: any) {
            console.error('Error loading attendance:', error);
            toast.error('Failed to load attendance');
        } finally {
            setLoading(false);
        }
    };

    const getStatus = (day: number): AttendanceStatus => {
        const dateKey = formatDate(year, month, day);

        // Check if there's a pending change
        if (changes[dateKey]) {
            return changes[dateKey];
        }

        // Check stored attendance
        if (attendance?.days[dateKey]) {
            return attendance.days[dateKey];
        }

        // If not in deployment period, return 'undeployed'
        if (!checkIsDeployed(day)) {
            return 'undeployed';
        }

        return 'present';
    };

    const handleDayClick = (day: number) => {
        // Can only mark up to today if it's the current month
        if (year === currentDateInfo.year && month === currentDateInfo.month && day > currentDateInfo.day) return;

        if (!checkIsDeployed(day)) return; // Can't mark undeployed days

        const dateKey = formatDate(year, month, day);
        const currentStatus = getStatus(day);

        // Cycle through statuses: present -> absent -> leave -> present
        let newStatus: AttendanceStatus;
        if (currentStatus === 'present') {
            newStatus = 'absent';
        } else if (currentStatus === 'absent') {
            newStatus = 'leave';
        } else {
            newStatus = 'present';
        }

        setChanges(prev => ({
            ...prev,
            [dateKey]: newStatus,
        }));
    };

    const handleSave = async () => {
        if (Object.keys(changes).length === 0) {
            toast('No changes to save');
            return;
        }

        setSaving(true);
        try {
            console.log('Saving attendance changes:', changes);
            // Save each changed day (pass guard data for salary calculation)
            for (const [date, status] of Object.entries(changes)) {
                console.log(`Saving ${date} as ${status} for guard ${guard.id}`);
                await markAttendance(guard.id, date, status, companyId, guard);
            }

            console.log('All attendance changes saved successfully');
            toast.success('Attendance updated successfully');
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error('Error saving attendance:', error);
            console.error('Error details:', {
                code: error.code,
                message: error.message,
                guardId: guard.id,
                companyId: companyId,
                changes: changes
            });
            toast.error(`Failed to save attendance: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Calculate first day of month (0 = Sunday, 6 = Saturday)
    const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Mark Attendance - ${new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={saving || loading}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </>
            }
        >
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <GuardInfo>
                    {guard.image ? (
                        <GuardImage src={guard.image} alt={guard.name} />
                    ) : (
                        <div
                            style={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                backgroundColor: theme.colors.light.background.tertiary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: theme.typography.fontSize['2xl'],
                                color: theme.colors.light.text.muted,
                            }}
                        >
                            {guard.name?.charAt(0) || '?'}
                        </div>
                    )}
                    <GuardDetails>
                        <GuardName>{guard.name || 'Unknown Guard'}</GuardName>
                        <GuardMeta>
                            {guard.guardId || 'No ID'} {guard.cnic ? `• ${guard.cnic}` : ''}
                        </GuardMeta>
                    </GuardDetails>
                </GuardInfo>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: theme.spacing[6] }}>
                        <p>Loading attendance...</p>
                    </div>
                ) : (
                    <>
                        <CalendarGrid>
                            {weekDays.map(day => (
                                <DayHeader key={day}>{day}</DayHeader>
                            ))}

                            {/* Empty cells for days before month starts */}
                            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
                                <div key={`empty-${i}`} />
                            ))}

                            {/* Actual days */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                                const day = i + 1;
                                const status = getStatus(day);
                                const isDeployedNow = checkIsDeployed(day);

                                // Disable if future day or not in deployment period
                                const isFuture = year > currentDateInfo.year ||
                                    (year === currentDateInfo.year && month > currentDateInfo.month) ||
                                    (year === currentDateInfo.year && month === currentDateInfo.month && day > currentDateInfo.day);

                                const isDisabled = isFuture || !isDeployedNow;
                                const isToday = year === currentDateInfo.year && month === currentDateInfo.month && day === currentDateInfo.day;

                                return (
                                    <DayCell
                                        key={day}
                                        status={status}
                                        disabled={isDisabled}
                                        isToday={isToday}
                                        onClick={() => handleDayClick(day)}
                                    >
                                        <DayNumber>{day}</DayNumber>
                                        {!isDisabled && (
                                            <StatusBadge status={status}>
                                                {status === 'present' ? 'P' : status === 'absent' ? 'A' : status === 'leave' ? 'L' : 'UD'}
                                            </StatusBadge>
                                        )}
                                    </DayCell>
                                );
                            })}
                        </CalendarGrid>

                        <Legend>
                            <LegendItem>
                                <LegendColor color={theme.colors.accent.success} />
                                <span>Present (P)</span>
                            </LegendItem>
                            <LegendItem>
                                <LegendColor color={theme.colors.accent.danger} />
                                <span>Absent (A)</span>
                            </LegendItem>
                            <LegendItem>
                                <LegendColor color={theme.colors.accent.warning} />
                                <span>Leave (L)</span>
                            </LegendItem>
                            <LegendItem>
                                <LegendColor color={theme.colors.light.background.tertiary} />
                                <span>Undeployed (UD)</span>
                            </LegendItem>
                        </Legend>

                        {Object.keys(changes).length > 0 && (
                            <div style={{
                                marginTop: theme.spacing[4],
                                padding: theme.spacing[3],
                                background: theme.colors.primary[50],
                                borderRadius: theme.borderRadius.md,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.primary[700],
                            }}>
                                {Object.keys(changes).length} day(s) modified. Click "Save Changes" to apply.
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default AttendanceModal;
